import "./styles.css";

import { SegmentRequestError, requestSegmentAudio } from "./api";
import { AudioEncoderClient } from "./audio-encoder-client";
import { buildExportFilename, triggerDownload } from "./download";
import {
  DEFAULT_UI_LOCALE,
  SUPPORTED_UI_LOCALES,
  UI_LANGUAGE_LABELS,
  UI_MESSAGES,
  getDefaultTtsLocale,
  getAvailableVoiceGroups,
  getTtsLanguageLabel,
  isUiLocale,
  prioritizeVoiceGroupsForUiLocale,
  resolveUiLocale,
  type UiLocale,
} from "./i18n";
import type { VoiceGroup, VoiceOption, StatusKind } from "./types";
import {
  CHUNK_RETRY_LIMIT,
  MAX_CHUNK_CHARS,
  MAX_CONCURRENCY,
} from "../shared/constants";
import { buildLrc, buildSrt } from "../shared/subtitles";
import { chunkText } from "../shared/text";
import type { SegmentMeta, TtsConfig } from "../shared/types";

const textInput = document.getElementById("text-input") as HTMLTextAreaElement;
const charCount = document.getElementById("char-count") as HTMLElement;
const uiLanguageSelect = document.getElementById("ui-language-select") as unknown as HTMLSelectElement;
const regionSelect = document.getElementById("region-select") as unknown as HTMLSelectElement;
const voiceSelect = document.getElementById("voice-select") as unknown as HTMLSelectElement;
const rateSlider = document.getElementById("rate-slider") as HTMLInputElement;
const pitchSlider = document.getElementById("pitch-slider") as HTMLInputElement;
const volumeSlider = document.getElementById("volume-slider") as HTMLInputElement;
const rateValue = document.getElementById("rate-value") as HTMLElement;
const pitchValue = document.getElementById("pitch-value") as HTMLElement;
const volumeValue = document.getElementById("volume-value") as HTMLElement;
const generatePanel = document.getElementById("generate-panel") as HTMLElement;
const generateBtn = document.getElementById("generate-btn") as HTMLButtonElement;
const btnText = generateBtn.querySelector(".btn-text") as HTMLElement;
const btnLoading = generateBtn.querySelector(".btn-loading") as HTMLElement;
const statusBadge = document.getElementById("status-badge") as HTMLElement;
const progressFill = document.getElementById("progress-fill") as HTMLElement;
const progressLabel = document.getElementById("progress-label") as HTMLElement;
const progressText = document.getElementById("progress-text") as HTMLElement;
const errorBox = document.getElementById("error-box") as HTMLElement;
const resultBox = document.getElementById("result-box") as HTMLElement;
const audioPlayer = document.getElementById("audio-player") as HTMLAudioElement;
const subtitlePreview = document.getElementById("subtitle-preview") as HTMLElement;
const downloadMp3Btn = document.getElementById("download-mp3-btn") as HTMLButtonElement;
const downloadSrtBtn = document.getElementById("download-srt-btn") as HTMLButtonElement;
const downloadLrcBtn = document.getElementById("download-lrc-btn") as HTMLButtonElement;
const themeToggle = document.getElementById("theme-toggle") as HTMLButtonElement;

const THEME_STORAGE_KEY = "sagatts-theme";
const UI_LOCALE_STORAGE_KEY = "sagatts-ui-locale";
const systemThemeQuery = window.matchMedia("(prefers-color-scheme: dark)");

type ThemePreference = "light" | "dark";

const voiceGroups: VoiceGroup[] = getAvailableVoiceGroups(
  Array.isArray(window.__voiceGroups) ? window.__voiceGroups : [],
);
const statusMessageKeys: Record<StatusKind, keyof typeof UI_MESSAGES[UiLocale]> = {
  idle: "idleStatus",
  running: "runningStatus",
  success: "successStatus",
  error: "errorStatus",
};

let currentUiLocale: UiLocale = DEFAULT_UI_LOCALE;
let currentRegionKey = "";
let currentVoiceName = "";
let currentMp3Blob: Blob | null = null;
let currentSrtText = "";
let currentLrcText = "";
let currentAudioUrl: string | null = null;
let currentExportBaseText = "";
let currentExportAt: Date | null = null;
let isBusy = false;
let currentStatusKind: StatusKind = "idle";
let currentProgressDone = 0;
let currentProgressTotal = 0;
let currentProgressMessageKey: keyof typeof UI_MESSAGES[UiLocale] = "waitingForText";

function getStoredTheme(): ThemePreference | null {
  const value = localStorage.getItem(THEME_STORAGE_KEY);
  return value === "light" || value === "dark" ? value : null;
}

function getSystemTheme(): ThemePreference {
  return systemThemeQuery.matches ? "dark" : "light";
}

function applyTheme(theme: ThemePreference): void {
  document.documentElement.dataset.theme = theme;
  themeToggle.setAttribute("aria-pressed", String(theme === "dark"));
  themeToggle.textContent =
    theme === "dark" ? UI_MESSAGES[currentUiLocale].themeLight : UI_MESSAGES[currentUiLocale].themeDark;
}

function initializeTheme(): void {
  applyTheme(getStoredTheme() ?? getSystemTheme());

  themeToggle.addEventListener("click", () => {
    const nextTheme = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
    localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    applyTheme(nextTheme);
  });

  systemThemeQuery.addEventListener("change", () => {
    if (!getStoredTheme()) {
      applyTheme(getSystemTheme());
    }
  });
}

function setStatus(kind: StatusKind, text: string): void {
  currentStatusKind = kind;
  statusBadge.className = `status-badge ${kind}`;
  statusBadge.textContent = text;
}

function setProgress(done: number, total: number, label: string): void {
  currentProgressDone = done;
  currentProgressTotal = total;
  const ratio = total === 0 ? 0 : Math.min(1, done / total);
  progressFill.style.width = `${ratio * 100}%`;
  progressLabel.textContent = total === 0 ? UI_MESSAGES[currentUiLocale].idleProgress : `${Math.round(ratio * 100)}%`;
  progressText.textContent = label;
}

function setProgressMessageKey(
  done: number,
  total: number,
  key: keyof typeof UI_MESSAGES[UiLocale],
): void {
  currentProgressMessageKey = key;
  setProgress(done, total, UI_MESSAGES[currentUiLocale][key]);
}

function updateGenerateAvailability(): void {
  const enabled = textInput.value.trim().length > 0 && !isBusy;
  generateBtn.disabled = !enabled;
  generatePanel.classList.toggle("disabled", !enabled);
  generatePanel.setAttribute("aria-disabled", String(!enabled));
  generatePanel.tabIndex = enabled ? 0 : -1;
}

function setBusy(busy: boolean): void {
  isBusy = busy;
  btnText.style.display = busy ? "none" : "inline";
  btnLoading.style.display = busy ? "inline" : "none";
  updateGenerateAvailability();
}

function showError(message: string): void {
  errorBox.textContent = message;
  errorBox.classList.remove("hidden");
}

function clearError(): void {
  errorBox.textContent = "";
  errorBox.classList.add("hidden");
}

function formatError(error: unknown): string {
  if (error instanceof SegmentRequestError) {
    if (error.code === "DAILY_QUOTA_EXCEEDED") {
      const remaining = error.details?.remaining ?? 0;
      const limit = error.details?.limit ?? 30000;
      return UI_MESSAGES[currentUiLocale].dailyQuotaExceededError
        .replace("{remaining}", String(remaining))
        .replace("{limit}", String(limit));
    }

    return error.message || UI_MESSAGES[currentUiLocale].unknownSegmentError;
  }

  return (error as Error).message || UI_MESSAGES[currentUiLocale].unknownSegmentError;
}

function resetResult(): void {
  resultBox.classList.add("hidden");
  subtitlePreview.textContent = "";
  currentMp3Blob = null;
  currentSrtText = "";
  currentLrcText = "";
  currentExportBaseText = "";
  currentExportAt = null;
  if (currentAudioUrl) {
    URL.revokeObjectURL(currentAudioUrl);
    currentAudioUrl = null;
  }
  audioPlayer.removeAttribute("src");
  audioPlayer.load();
}

function updateStats(): void {
  const text = textInput.value.slice(0, 50000);
  if (text !== textInput.value) {
    textInput.value = text;
  }
  charCount.textContent = String(text.length);
  updateGenerateAvailability();
}

function updateSliderLabels(): void {
  rateValue.textContent = `${rateSlider.value}%`;
  pitchValue.textContent = `${pitchSlider.value}%`;
  volumeValue.textContent = `${volumeSlider.value}%`;
}

function formatVoiceLabel(voice: VoiceOption): string {
  const gender = voice.gender === "Female" ? UI_MESSAGES[currentUiLocale].female : UI_MESSAGES[currentUiLocale].male;
  return `${voice.displayName} · ${gender}`;
}

function setSelectOptions(
  select: HTMLSelectElement,
  items: { value: string; label: string }[],
  selectedValue: string,
): void {
  select.innerHTML = "";

  items.forEach((item) => {
    const option = document.createElement("option");
    option.value = item.value;
    option.textContent = item.label;
    option.selected = item.value === selectedValue;
    select.appendChild(option);
  });
}

function selectVoice(voice: VoiceOption): void {
  currentVoiceName = voice.shortName;
  voiceSelect.value = voice.shortName;
}

function selectRegion(group: VoiceGroup): void {
  currentRegionKey = group.key;
  regionSelect.value = group.key;

  setSelectOptions(
    voiceSelect,
    group.voices.map((voice) => ({
      value: voice.shortName,
      label: formatVoiceLabel(voice),
    })),
    currentVoiceName,
  );
  voiceSelect.disabled = group.voices.length === 0;

  const preferredVoice =
    group.voices.find((voice) => voice.shortName === currentVoiceName) ??
    group.voices[0];

  if (preferredVoice) {
    selectVoice(preferredVoice);
    return;
  }

  currentVoiceName = "";
  voiceSelect.value = "";
}

function setDocumentText(): void {
  const messages = UI_MESSAGES[currentUiLocale];
  document.documentElement.lang = currentUiLocale;
  document.title = messages.appTitle;

  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((element) => {
    const key = element.dataset.i18n as keyof typeof messages | undefined;
    if (key && typeof messages[key] === "string") {
      element.textContent = messages[key];
    }
  });

  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-i18n-placeholder]").forEach((element) => {
    const key = element.dataset.i18nPlaceholder as keyof typeof messages | undefined;
    if (key && typeof messages[key] === "string") {
      element.placeholder = messages[key];
    }
  });

  applyTheme((document.documentElement.dataset.theme as ThemePreference | undefined) ?? getSystemTheme());
}

function setUiLocale(locale: UiLocale, persist: boolean): void {
  currentUiLocale = locale;
  if (persist) {
    localStorage.setItem(UI_LOCALE_STORAGE_KEY, locale);
  }
  setDocumentText();
  populateUiLanguageSelector();
  populateVoiceSelectors(currentRegionKey);
  setStatus(currentStatusKind, UI_MESSAGES[currentUiLocale][statusMessageKeys[currentStatusKind]]);
  setProgressMessageKey(currentProgressDone, currentProgressTotal, currentProgressMessageKey);
}

function getPathUiLocale(): UiLocale | null {
  const [, pathLocale] = window.location.pathname.split("/");
  return isUiLocale(pathLocale) ? pathLocale : null;
}

function getInitialUiLocale(): UiLocale {
  const pathLocale = getPathUiLocale();
  if (pathLocale) {
    return pathLocale;
  }

  const stored = localStorage.getItem(UI_LOCALE_STORAGE_KEY);
  if (isUiLocale(stored)) {
    return stored;
  }

  return resolveUiLocale(navigator.languages?.length ? navigator.languages : [navigator.language]);
}

function populateUiLanguageSelector(): void {
  setSelectOptions(
    uiLanguageSelect,
    SUPPORTED_UI_LOCALES.map((locale) => ({
      value: locale,
      label: UI_LANGUAGE_LABELS[locale],
    })),
    currentUiLocale,
  );
}

function buildLocalePath(locale: UiLocale): string {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const suffix = `${window.location.search}${window.location.hash}`;
  if (segments.length > 0 && isUiLocale(segments[0])) {
    segments[0] = locale;
    const trailingSlash = window.location.pathname.endsWith("/") ? "/" : "";
    return `/${segments.join("/")}${trailingSlash}${suffix}`;
  }

  return `/${locale}/${suffix}`;
}

function initializeUiLanguageSelector(): void {
  populateUiLanguageSelector();
  uiLanguageSelect.addEventListener("change", () => {
    if (isUiLocale(uiLanguageSelect.value)) {
      const nextLocale = uiLanguageSelect.value;
      localStorage.setItem(UI_LOCALE_STORAGE_KEY, nextLocale);
      window.location.assign(buildLocalePath(nextLocale));
    }
  });
}

function populateVoiceSelectors(preferredRegionKey = currentRegionKey): void {
  const orderedVoiceGroups = prioritizeVoiceGroupsForUiLocale(
    voiceGroups,
    currentUiLocale,
  );

  setSelectOptions(
    regionSelect,
    orderedVoiceGroups.map((group) => ({
      value: group.key,
      label: getTtsLanguageLabel(group, currentUiLocale),
    })),
    preferredRegionKey,
  );

  const defaultGroup =
    voiceGroups.find((group) => group.key === preferredRegionKey) ??
    voiceGroups.find(
      (group) => group.key === getDefaultTtsLocale(voiceGroups, currentUiLocale),
    ) ??
    voiceGroups[0];
  if (defaultGroup) {
    selectRegion(defaultGroup);
  }
}

function initializeVoiceSelectors(): void {
  populateVoiceSelectors();
  regionSelect.addEventListener("change", () => {
    const group = voiceGroups.find((item) => item.key === regionSelect.value);
    if (group) {
      currentVoiceName = "";
      selectRegion(group);
    }
  });

  voiceSelect.addEventListener("change", () => {
    const group = voiceGroups.find((item) => item.key === currentRegionKey);
    const voice = group?.voices.find((item) => item.shortName === voiceSelect.value);
    if (voice) {
      selectVoice(voice);
    }
  });
}

function getConfig(): TtsConfig {
  return {
    voiceName: currentVoiceName,
    locale: currentRegionKey,
    rate: Number(rateSlider.value),
    pitch: Number(pitchSlider.value),
    volume: Number(volumeSlider.value),
  };
}

async function retrySegmentRequest(
  text: string,
  config: TtsConfig,
): Promise<ArrayBuffer> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= CHUNK_RETRY_LIMIT; attempt += 1) {
    try {
      return await requestSegmentAudio(text, config);
    } catch (error) {
      if (
        error instanceof SegmentRequestError &&
        error.code === "DAILY_QUOTA_EXCEEDED"
      ) {
        throw error;
      }

      lastError = error as Error;
      if (attempt === CHUNK_RETRY_LIMIT) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }

  throw lastError ?? new Error(UI_MESSAGES[currentUiLocale].unknownSegmentError);
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;

  async function runNext(): Promise<void> {
    const index = cursor;
    cursor += 1;
    if (index >= items.length) {
      return;
    }
    await worker(items[index], index);
    await runNext();
  }

  const runners = Array.from({ length: Math.min(concurrency, items.length) }, () =>
    runNext(),
  );
  await Promise.all(runners);
}

async function generate(): Promise<void> {
  clearError();
  resetResult();

  const text = textInput.value.trim();
  if (!text) {
    showError(UI_MESSAGES[currentUiLocale].emptyTextError);
    return;
  }

  if (!currentVoiceName) {
    showError(UI_MESSAGES[currentUiLocale].emptyVoiceError);
    return;
  }

  const segments = chunkText(text, MAX_CHUNK_CHARS);
  if (segments.length === 0) {
    showError(UI_MESSAGES[currentUiLocale].emptyProcessedTextError);
    return;
  }

  setBusy(true);
  setStatus("running", UI_MESSAGES[currentUiLocale].runningStatus);
  setProgressMessageKey(0, segments.length, "generatingAudio");

  const config = getConfig();
  const encoder = new AudioEncoderClient();
  const metaByIndex = new Map<number, SegmentMeta>();
  let completedSegments = 0;

  try {
    await runWithConcurrency(segments, MAX_CONCURRENCY, async (segment, index) => {
      const buffer = await retrySegmentRequest(segment, config);
      encoder.addSegment(index, segment, buffer);
      completedSegments += 1;
      setProgress(
        completedSegments,
        segments.length,
        UI_MESSAGES[currentUiLocale].generatingAudio,
      );
    });

    const { mp3Buffer, meta } = await encoder.finalize(segments.length);
    meta.forEach((item, index) => {
      metaByIndex.set(item.index, {
        ...item,
        text: segments[index] ?? item.text,
      });
    });

    const orderedMeta = segments.map((segment, index) => ({
      index,
      text: segment,
      durationMs: metaByIndex.get(index)?.durationMs ?? 0,
    }));

    const mp3Blob = new Blob([mp3Buffer], { type: "audio/mpeg" });
    currentMp3Blob = mp3Blob;
    currentSrtText = buildSrt(orderedMeta);
    currentLrcText = buildLrc(orderedMeta);
    currentExportBaseText = text;
    currentExportAt = new Date();

    currentAudioUrl = URL.createObjectURL(mp3Blob);
    audioPlayer.src = currentAudioUrl;
    subtitlePreview.textContent = currentSrtText;
    resultBox.classList.remove("hidden");
    setStatus("success", UI_MESSAGES[currentUiLocale].successStatus);
    setProgressMessageKey(segments.length, segments.length, "completeProgress");
  } catch (error) {
    setStatus("error", UI_MESSAGES[currentUiLocale].errorStatus);
    setProgressMessageKey(0, segments.length, "failedProgress");
    showError(formatError(error));
  } finally {
    encoder.terminate();
    setBusy(false);
  }
}

textInput.addEventListener("input", updateStats);
rateSlider.addEventListener("input", updateSliderLabels);
pitchSlider.addEventListener("input", updateSliderLabels);
volumeSlider.addEventListener("input", updateSliderLabels);
generatePanel.addEventListener("click", () => {
  if (!generateBtn.disabled) {
    void generate();
  }
});
generatePanel.addEventListener("keydown", (event) => {
  if (generateBtn.disabled) {
    return;
  }
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    void generate();
  }
});
downloadMp3Btn.addEventListener("click", () => {
  if (currentMp3Blob) {
    triggerDownload(
      currentMp3Blob,
      buildExportFilename(
        currentExportBaseText,
        "mp3",
        currentExportAt ?? new Date(),
        UI_MESSAGES[currentUiLocale].filenameFallback,
      ),
    );
  }
});
downloadSrtBtn.addEventListener("click", () => {
  if (currentSrtText) {
    triggerDownload(
      new Blob([currentSrtText], { type: "application/x-subrip;charset=utf-8" }),
      buildExportFilename(
        currentExportBaseText,
        "srt",
        currentExportAt ?? new Date(),
        UI_MESSAGES[currentUiLocale].filenameFallback,
      ),
    );
  }
});
downloadLrcBtn.addEventListener("click", () => {
  if (currentLrcText) {
    triggerDownload(
      new Blob([currentLrcText], { type: "text/plain;charset=utf-8" }),
      buildExportFilename(
        currentExportBaseText,
        "lrc",
        currentExportAt ?? new Date(),
        UI_MESSAGES[currentUiLocale].filenameFallback,
      ),
    );
  }
});

currentUiLocale = getInitialUiLocale();
initializeTheme();
setDocumentText();
initializeUiLanguageSelector();
initializeVoiceSelectors();
updateStats();
updateSliderLabels();
setStatus("idle", UI_MESSAGES[currentUiLocale].idleStatus);
setProgressMessageKey(0, 0, "waitingForText");
